import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  BarChart3,
  ChevronDown,
  Clock3,
  Home,
  Inbox,
  Layers,
  LogOut,
  MapPin,
  PhoneCall,
  Plus,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useLandlordDashboard } from '@hooks/useLandlordDashboard';
import { Property, PropertyStatus } from '../../types/property.types';
import { PropertyInquiry } from '../../types/inquiry.types';
import PropertyMatchSuggestions from '@components/PropertyMatchSuggestions';

const LandlordDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userRole, logout } = useAuth();
  const { landlord, properties, inquiries, loading, error } = useLandlordDashboard(currentUser?.uid);
  const [showMatchesForProperty, setShowMatchesForProperty] = useState<string | null>(null);

  const greetingName =
    landlord?.businessName ||
    landlord?.contactName ||
    currentUser?.email?.split('@')?.[0] ||
    'Landlord';

  const activeProperties = useMemo(
    () => properties.filter((property) => property.status !== 'draft'),
    [properties]
  );

  const summary = useMemo(() => {
    const available = properties.filter((p) => p.status === 'available').length;
    const rented = properties.filter((p) => p.status === 'rented').length;
    const pending = properties.filter((p) => p.status === 'pending').length;
    const averageRent =
      activeProperties.length > 0
        ? Math.round(
            activeProperties.reduce((sum, property) => sum + (property.monthlyRent || 0), 0) /
              activeProperties.length
          )
        : 0;
    const newInquiryCount = inquiries.filter((inq) => inq.status === 'new').length;
    const totalViews = properties.reduce(
      (sum, property) => sum + ((property as any).views ?? 0),
      0
    );
    const occupancyRate =
      properties.length > 0 ? Math.round(((properties.length - available) / properties.length) * 100) : 0;

    return {
      available,
      rented,
      pending,
      averageRent,
      newInquiryCount,
      totalViews,
      occupancyRate,
    };
  }, [activeProperties, inquiries, properties]);

  const recentInquiries = useMemo(() => {
    return [...inquiries]
      .sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() ?? 0) -
          (a.createdAt?.toMillis?.() ?? 0)
      )
      .slice(0, 5);
  }, [inquiries]);

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="card p-8 text-center max-w-lg w-full">
          <h1 className="text-3xl font-extrabold text-text-primary mb-2">Landlord workspace</h1>
          <p className="text-text-secondary mb-6">Sign in to view your listings and inquiries.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button className="btn-primary w-full sm:w-auto" onClick={() => navigate('/login')}>
              Go to login
            </button>
            <button className="btn-secondary w-full sm:w-auto" onClick={() => navigate('/signup?role=landlord')}>
              Create landlord account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (userRole && userRole !== 'landlord') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="card p-8 text-center max-w-lg w-full">
          <h1 className="text-3xl font-extrabold text-text-primary mb-2">Switch to landlord</h1>
          <p className="text-text-secondary mb-6">This dashboard is reserved for landlord accounts.</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 left-8 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <header className="bg-white/80 backdrop-blur-md border border-gray-100 shadow-lg rounded-2xl px-3 sm:px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/landlord/dashboard')}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary-200 to-primary-300 flex items-center justify-center shadow-lg hover:shadow-xl transition-all shrink-0 cursor-pointer"
                aria-label="Go to dashboard home"
              >
                <Home className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-extrabold text-text-primary truncate">{greetingName}</h1>
                <p className="text-xs text-text-muted">Landlord Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="btn-secondary py-2 px-3 sm:px-4 rounded-xl text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2"
                onClick={() => navigate('/landlord/properties')}
              >
                <Layers className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">My properties</span><span className="sm:hidden">Properties</span>
              </button>
              <button
                className="btn-primary py-2 px-3 sm:px-4 rounded-xl text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2"
                onClick={() => navigate('/landlord/create-property')}
              >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="hidden sm:inline">New listing</span><span className="sm:hidden">New</span>
              </button>
            </div>
          </div>
        </header>

        {error && (
          <div className="border-l-4 border-red-400 bg-red-50 text-red-700 px-4 py-3 rounded-xl shadow-sm">
            <p className="font-semibold">We couldn't load everything.</p>
            <p className="text-sm">Firestore replied with: {error}. Check permissions or indexes.</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 card p-4 sm:p-6 bg-white/90">
            <div className="mb-4">
              <h2 className="text-xl sm:text-2xl font-extrabold text-text-primary mb-1">Portfolio</h2>
              <p className="text-text-secondary text-sm">
                {activeProperties.length} active • {summary.newInquiryCount} new inquiries
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              <StatCard
                title="Active listings"
                value={activeProperties.length}
                icon={<BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />}
                accent="from-primary-200 to-primary-300"
                helper={`${summary.available} available / ${summary.pending} pending`}
              />
              <StatCard
                title="Average rent"
                value={summary.averageRent > 0 ? `R${summary.averageRent.toLocaleString()}` : '—'}
                icon={<TrendingUp className="w-5 h-5" />}
                accent="from-accent-purple to-primary-300"
                helper="Based on active listings"
              />
              <StatCard
                title="New inquiries"
                value={summary.newInquiryCount}
                icon={<Inbox className="w-5 h-5" />}
                accent="from-accent-blue to-accent-teal"
                helper="Awaiting your response"
              />
              <StatCard
                title="Occupancy"
                value={`${summary.occupancyRate}%`}
                icon={<Clock3 className="w-5 h-5" />}
                accent="from-primary-300 to-accent-purple"
                helper={`${summary.rented} rented / ${properties.length} total`}
              />
            </div>
          </div>

          <div className="card p-4 sm:p-6 bg-white/90">
            <h3 className="text-lg sm:text-xl font-bold text-text-primary mb-4">Activity</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between p-3 rounded-xl bg-primary-50 border border-primary-100">
                <div className="flex items-center gap-2 text-text-secondary">
                  <Sparkles className="w-4 h-4 text-primary-300" />
                  Total views
                </div>
                <span className="font-bold text-text-primary">{summary.totalViews.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-accent-blue/10 border border-accent-blue/30">
                <div className="flex items-center gap-2 text-text-secondary">
                  <Inbox className="w-4 h-4 text-accent-blue" />
                  Inquiries to answer
                </div>
                <span className="font-bold text-text-primary">{summary.newInquiryCount}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-accent-purple/10 border border-accent-purple/30">
                <div className="flex items-center gap-2 text-text-secondary">
                  <PhoneCall className="w-4 h-4 text-accent-purple" />
                  Follow-ups scheduled
                </div>
                <span className="font-bold text-text-primary">
                  {inquiries.filter((inq) => inq.status === 'scheduled').length}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 card p-4 sm:p-6 bg-white/90">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-text-primary">Properties</h3>
                <p className="text-text-secondary text-sm">{properties.length} total</p>
              </div>
              <button
                className="btn-outline py-2 px-3 sm:px-4 rounded-xl text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 shrink-0"
                onClick={() => navigate('/landlord/create-property')}
              >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Add
              </button>
            </div>

            {loading ? (
              <div className="h-32 flex items-center justify-center text-text-secondary">Loading your portfolio...</div>
            ) : properties.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center space-y-2">
                <h4 className="text-lg font-semibold text-text-primary">No listings yet</h4>
                <p className="text-text-secondary text-sm">
                  Create your first listing to start receiving tenant interest.
                </p>
                <button className="btn-primary" onClick={() => navigate('/landlord/create-property')}>
                  Create listing
                </button>
              </div>
            ) : (
              <div className="grid gap-3">
                {[...properties]
                  .sort(
                    (a, b) =>
                      (b.updatedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0) -
                      (a.updatedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0)
                  )
                  .map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
              </div>
            )}
          </div>

          <div className="card p-4 sm:p-6 bg-white/90">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-text-primary">Inquiries</h3>
                <p className="text-text-secondary text-sm">{inquiries.length} total</p>
              </div>
              <button
                className="btn-outline py-2 px-3 sm:px-4 rounded-xl text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 shrink-0"
                onClick={() => navigate('/landlord/inquiries')}
              >
                <Inbox className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> All
              </button>
            </div>

            {loading ? (
              <div className="h-32 flex items-center justify-center text-text-secondary">Loading inquiries...</div>
            ) : recentInquiries.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center space-y-2">
                <h4 className="text-lg font-semibold text-text-primary">No inquiries yet</h4>
                <p className="text-text-secondary text-sm">New tenant questions will appear here in real time.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentInquiries.map((inquiry) => (
                  <InquiryCard key={inquiry.id} inquiry={inquiry} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card p-4 sm:p-6 bg-white/90">
          <div className="mb-4">
            <h3 className="text-lg sm:text-xl font-bold text-text-primary mb-1">Tenant Matches</h3>
            <p className="text-text-secondary text-xs sm:text-sm">Compatible roommate pairs for your properties</p>
          </div>
          
          {loading ? (
            <div className="h-32 flex items-center justify-center text-text-secondary text-sm">Loading matches...</div>
          ) : properties.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 sm:p-6 text-center space-y-2">
              <h4 className="text-base sm:text-lg font-semibold text-text-primary">No properties yet</h4>
              <p className="text-text-secondary text-xs sm:text-sm">
                Create a property listing to discover potential tenant matches.
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-start sm:items-center gap-2 text-xs sm:text-sm text-text-secondary bg-accent-teal/10 border border-accent-teal/30 rounded-lg p-2.5 sm:p-3">
                <Sparkles className="w-4 h-4 text-accent-teal shrink-0 mt-0.5 sm:mt-0" />
                <span className="leading-snug sm:leading-normal">Select a property to see compatible tenant pairs</span>
              </div>
              
              <div className="grid gap-3 sm:gap-4">
                {properties
                  .filter(p => p.status === 'available')
                  .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0))
                  .map((property) => (
                    <div key={property.id} className="space-y-2 sm:space-y-3">
                      <button
                        onClick={() => setShowMatchesForProperty(
                          showMatchesForProperty === property.id ? null : property.id!
                        )}
                        aria-expanded={showMatchesForProperty === property.id}
                        className={`w-full text-left p-3 sm:p-4 rounded-xl border transition-all ${
                          showMatchesForProperty === property.id
                            ? 'bg-accent-teal/10 border-accent-teal/50 shadow-md'
                            : 'bg-white border-gray-200 hover:border-accent-teal/30 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 sm:gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-text-primary text-sm sm:text-base truncate pr-2">{property.title}</p>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1.5 sm:mt-1">
                              <p className="text-xs text-text-secondary flex items-center gap-1">
                                <MapPin className="w-3 h-3 shrink-0" />
                                <span className="truncate">{property.address.city}</span>
                              </p>
                              <span className="hidden sm:inline text-text-muted">•</span>
                              <p className="text-xs sm:text-sm font-bold text-primary-300">
                                R{property.monthlyRent.toLocaleString()}/mo
                              </p>
                            </div>
                          </div>
                          <ChevronDown
                            aria-hidden="true"
                            className={`w-4 h-4 sm:w-5 sm:h-5 transform transition-transform text-accent-teal shrink-0 ${
                              showMatchesForProperty === property.id ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </button>
                      
                      {showMatchesForProperty === property.id && property.id && (
                        <div className="border border-accent-teal/20 rounded-xl p-3 sm:p-4 bg-accent-teal/5">
                          <PropertyMatchSuggestions property={property} />
                        </div>
                      )}
                    </div>
                  ))}
                
                {properties.filter(p => p.status === 'available').length === 0 && (
                  <div className="col-span-full text-center py-6 sm:py-8 text-xs sm:text-sm text-text-secondary">
                    No available properties. Mark properties as available to see matches.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <div className="mt-8 pb-6">
          <button
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: string | number;
  helper: string;
  icon: React.ReactNode;
  accent: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, helper, icon, accent }) => (
  <div className="p-3 sm:p-4 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200">
    <div className="flex items-center justify-between mb-2 sm:mb-3">
      <p className="text-xs sm:text-sm font-semibold text-text-secondary truncate pr-1">{title}</p>
      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br ${accent} text-white flex items-center justify-center shadow-lg shrink-0`}>
        {icon}
      </div>
    </div>
    <p className="text-xl sm:text-2xl font-extrabold text-text-primary">{value}</p>
    <p className="text-xs text-text-muted mt-1 line-clamp-2">{helper}</p>
  </div>
);

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const status = property.status as PropertyStatus;
  const statusStyles: Record<PropertyStatus, string> = {
    available: 'bg-green-50 text-green-700 border-green-100',
    pending: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    rented: 'bg-blue-50 text-blue-700 border-blue-100',
    draft: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  return (
    <div className="border border-gray-100 rounded-xl p-3 sm:p-4 bg-white/60 hover:bg-white transition-colors shadow-sm hover:shadow-md">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
            <h4 className="text-base sm:text-lg font-bold text-text-primary truncate">{property.title}</h4>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${statusStyles[status]} shrink-0 self-start`}>
              {status}
            </span>
          </div>
          <p className="text-xs sm:text-sm text-text-secondary line-clamp-2">{property.description}</p>
          <div className="flex flex-wrap gap-2 mt-3 text-xs sm:text-sm text-text-secondary">
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary-50 text-primary-300 border border-primary-100">
              <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="truncate">{property.address.city}</span>
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 text-purple-700 border border-purple-100">
              <Layers className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              {property.propertyType}
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-accent-blue/10 text-accent-blue border border-accent-blue/30">
              R{property.monthlyRent.toLocaleString()}/mo
            </div>
          </div>
        </div>
        <div className="flex sm:flex-col gap-2 text-xs sm:text-sm text-text-secondary">
          <div className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-primary-50 text-primary-300 border border-primary-100 font-semibold whitespace-nowrap">
            {property.views ?? 0} views
          </div>
          <div className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-accent-purple/10 text-accent-purple border border-accent-purple/30 font-semibold whitespace-nowrap">
            {(property as any).inquiries ?? 0} inquiries
          </div>
        </div>
      </div>
      <div className="mt-3 text-xs text-text-muted">
        <span>
          Updated{' '}
          {property.updatedAt?.toDate
            ? formatDistanceToNow(property.updatedAt.toDate(), { addSuffix: true })
            : 'recently'}
        </span>
      </div>
    </div>
  );
};

interface InquiryCardProps {
  inquiry: PropertyInquiry;
}

const InquiryCard: React.FC<InquiryCardProps> = ({ inquiry }) => {
  const statusColors: Record<PropertyInquiry['status'], string> = {
    new: 'bg-primary-50 text-primary-300 border-primary-100',
    viewed: 'bg-blue-50 text-blue-700 border-blue-100',
    responded: 'bg-green-50 text-green-700 border-green-100',
    scheduled: 'bg-purple-50 text-accent-purple border-accent-purple/30',
    declined: 'bg-red-50 text-red-600 border-red-100',
    accepted: 'bg-green-100 text-green-800 border-green-200',
  };

  return (
    <div className="border border-gray-100 rounded-xl p-4 bg-white/60 hover:bg-white transition-colors shadow-sm hover:shadow-md">
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary line-clamp-1">
            {inquiry.message || 'New inquiry received'}
          </p>
          <p className="text-xs text-text-muted mt-1">
            {inquiry.createdAt?.toDate
              ? formatDistanceToNow(inquiry.createdAt.toDate(), { addSuffix: true })
              : 'Just now'}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusColors[inquiry.status]} ml-2 shrink-0`}>
          {inquiry.status}
        </span>
      </div>
    </div>
  );
};

export default LandlordDashboard;
